import { useState, type Dispatch, type SetStateAction } from "react"
import { BiRadioCircle, BiRadioCircleMarked } from "react-icons/bi"
import ReactCanvasConfetti from "react-canvas-confetti"
import { animated, useTransition } from "react-spring"
import { useDrag } from "react-use-gesture"

import onyxandrose from "@src/entities"
import { colors } from "@src/utilities/confetti"
import classNames from "@src/utilities/class-names"
import { formatCurrency } from "@src/utilities/format-numbers"
import siteUrl from "@src/utilities/get-site-url"
import optimizeImage from "@src/utilities/optimize-image"

import Button from "@src/components/Button"
import RitualFrequencySelect from "@src/components/ritual-frequency-select"
import ProductHeaderLinks from "@src/components/ProductHeaderLinks"
import QuantitySelector from "@src/components/QuantitySelector"

const defaultTransition = {}

export interface ProductHeaderProps {
  amazonUrl: string | null
  featuredImages: string[]
  inStock: boolean
  isAddingToBag: boolean
  isReady: boolean
  minimumQuantityForSubscription: number | null
  onAddToCart: () => void
  onChangeFrequency: (newFrequency: number) => void
  onChangeSelectedPurchaseType: (
    newType: onyxandrose.Types.CartItemPurchaseType
  ) => void
  onChangeQuantity: Dispatch<SetStateAction<number>>
  originalRetailPrice: number | null
  oneTimePurchasePrice: number | null
  productIconLinks?: any[]
  productLongDescription: string
  productName?: string | null
  productPageUrl: string
  productShortDescription?: string | null
  productSku?: string
  selectedQuantity: number
  selectedFrequencyInWeeks: number
  selectedPurchaseType: onyxandrose.Types.CartItemPurchaseType
  subscriptionPurchasePrice: number | null
  wholesalePurchasePrice: number | null
  yotpoId?: string | null
}

export default function ProductHeader({
  amazonUrl,
  featuredImages,
  inStock,
  isAddingToBag,
  isReady,
  minimumQuantityForSubscription,
  onAddToCart,
  onChangeFrequency,
  onChangeSelectedPurchaseType,
  onChangeQuantity,
  originalRetailPrice,
  oneTimePurchasePrice,
  productIconLinks,
  productLongDescription,
  productName,
  productPageUrl,
  productShortDescription,
  productSku,
  selectedFrequencyInWeeks,
  selectedQuantity,
  selectedPurchaseType,
  subscriptionPurchasePrice,
}: ProductHeaderProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0)

  const transitions = useTransition(
    featuredImages[currentSlideIndex],
    defaultTransition
  )

  const goToNextSlide = () =>
    setCurrentSlideIndex((prevState) =>
      prevState === featuredImages.length - 1 ? 0 : prevState + 1
    )

  const goToPreviousSlide = () =>
    setCurrentSlideIndex((prevState) =>
      prevState === 0 ? featuredImages.length - 1 : prevState - 1
    )

  const bindDrag = useDrag(
    ({ down, movement: [mx] }) => {
      if (down) {
        return
      }
      if (mx < 0) {
        goToPreviousSlide()
      } else {
        goToNextSlide()
      }
    },
    { axis: "x" }
  )

  return (
    <>
      <div className="mb-8 md:hidden" data-component="product-header">
        {transitions((springStyle, featuredImage, key) => (
          <animated.div
            {...bindDrag()}
            key={`slide-${key}`}
            style={springStyle}
          >
            <div
              className="bg-center bg-cover before:content-[''] before:block before:pt-[125%]"
              style={{
                backgroundImage: `url(${featuredImage})`,
              }}
            />
          </animated.div>
        ))}
      </div>
      <div className="container-desktop">
        <div className="md:flex md:items-start">
          <div className="hidden md:block md:flex-1 md:mb-16 lg:flex lg:flex-[3_0_0%] lg:flex-row">
            <div className="hidden items-start lg:block lg:pr-8">
              {featuredImages.map((image, idx) => (
                <div
                  className={classNames(
                    "bg-center bg-cover w-24 before:content-[''] before:block before:pt-[100%] cursor-pointer",
                    idx === 0 ? "" : "mt-2"
                  )}
                  key={`thumbnail_${idx}`}
                  onClick={() => setCurrentSlideIndex(idx)}
                  style={{
                    backgroundImage: `url(${optimizeImage(image, 300)})`,
                  }}
                />
              ))}
            </div>
            <div className="grow">
              {transitions((springStyle, featuredImage, key) => (
                <animated.div
                  {...bindDrag()}
                  key={`slide-${key}`}
                  style={springStyle}
                >
                  <div
                    className="bg-center bg-cover mr-8 before:content-[''] before:block before:pt-[125%]"
                    style={{
                      backgroundImage: `url(${optimizeImage(
                        featuredImage,
                        1600
                      )})`,
                    }}
                  />
                </animated.div>
              ))}
            </div>
          </div>
          <div className="mb-16 md:mt-8 md:flex-[1_0_0%] lg:flex-[2_0_0%]">
            {amazonUrl && (
              <div className="grid grid-cols-4 gap-4 mb-1 items-center">
                <h1
                  className="font-extrabold text-2xl col-span-3 mt-1 md:text-3xl lg:text-4xl uppercase"
                  dangerouslySetInnerHTML={{
                    __html: productName ?? "Enter a Product Name",
                  }}
                />
                <a href={amazonUrl}>
                  <img src="/images/available_at_amazon.png" />
                </a>
              </div>
            )}
            {!amazonUrl && (
              <h1
                className="font-extrabold mb-1 text-2xl md:text-3xl lg:text-4xl uppercase"
                dangerouslySetInnerHTML={{
                  __html: productName ?? "Enter a Product Name",
                }}
              />
            )}
            {productShortDescription && (
              <h2
                className="font-serif italic tracking-wide mb-2 text-lg"
                dangerouslySetInnerHTML={{ __html: productShortDescription }}
              />
            )}
            {productSku && (
              <div
                className="yotpo bottomLine"
                data-appkey={process.env.NEXT_PUBLIC_YOTPO_APP_KEY}
                data-domain={process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}
                data-product-id={productSku}
                data-name={productName}
                data-url={`${siteUrl()}/${productPageUrl}`}
                data-description={productShortDescription}
              />
            )}
            <hr className="bg-gray-300 my-4" />
            <div
              className="prose font-serif prose-lg"
              dangerouslySetInnerHTML={{ __html: productLongDescription }}
            />
            {productIconLinks &&
              productIconLinks.length > 0 &&
              productIconLinks.map((link) => (
                <ProductHeaderLinks
                  currentSlug={productPageUrl}
                  header={link.header}
                  links={link.links}
                  linksKey={link._uid}
                />
              ))}
            {amazonUrl && (
              <div className="grid grid-cols-2 gap-4 items-center mt-4">
                <a
                  className="bg-black border border-black inline-block rounded-sm py-3 px-8 text-white text-sm font-browser font-medium uppercase text-center hover:bg-yellow-400 transition-colors hover:text-black"
                  href={amazonUrl}
                >
                  Shop Now on Amazon
                </a>
                <div className="">
                  <img
                    src="/images/available_at_amazon.png"
                    className="h-8 inline-block"
                  />
                </div>
              </div>
            )}
            {!amazonUrl && <hr className="bg-gray-300 my-4" />}
            {!amazonUrl && !inStock && (
              <div className="bg-red/20 border border-red font-browser font-bold p-4 text-red text-sm">
                This item is currently out of stock. Check back soon!
              </div>
            )}
            {!amazonUrl &&
              inStock &&
              subscriptionPurchasePrice === null &&
              oneTimePurchasePrice && (
                <>
                  <div data-product-header-data-is-loading={!isReady}>
                    <h3 className="font-bold text-lg">
                      Purchase Once - {formatCurrency(oneTimePurchasePrice)}{" "}
                      {originalRetailPrice &&
                        originalRetailPrice > oneTimePurchasePrice && (
                          <small className="text-orange">
                            (
                            <span className="text-gray-400 line-through">
                              {formatCurrency(originalRetailPrice)}
                            </span>{" "}
                            - save{" "}
                            {formatCurrency(
                              originalRetailPrice! - oneTimePurchasePrice
                            )}
                            )
                          </small>
                        )}
                    </h3>
                    <p className="hidden font-sans text-sm tracking-wide">
                      or 4 interest-free payments of{" "}
                      <strong>
                        {formatCurrency(
                          0.25 * oneTimePurchasePrice * selectedQuantity
                        )}
                      </strong>{" "}
                      with sezzle
                    </p>
                  </div>
                  <div className="font-browser text-lg mt-4 mb-8 w-[8rem]">
                    <p className="font-bold mb-1 text-sm">Quantity</p>
                    <QuantitySelector
                      onSetQuantity={onChangeQuantity}
                      quantity={selectedQuantity}
                    />
                  </div>
                  <div className="mt-8 mb-4">
                    <Button
                      disabled={isAddingToBag || !isReady}
                      onClick={() => onAddToCart()}
                      size="small"
                      text={
                        isAddingToBag
                          ? "Adding to your cart"
                          : `Add to cart | ${formatCurrency(
                              oneTimePurchasePrice
                            )}`
                      }
                    />
                  </div>
                </>
              )}
            {!amazonUrl &&
              inStock &&
              subscriptionPurchasePrice &&
              oneTimePurchasePrice && (
                <div>
                  <p className="font-sans text-sm tracking-wide font-extrabold mb-2">
                    Purchase Options
                  </p>
                  <div
                    className="items-start flex cursor-pointer"
                    onClick={() => onChangeSelectedPurchaseType("subscription")}
                  >
                    <div className="pt-1 pr-1">
                      {selectedPurchaseType === "subscription" ? (
                        <BiRadioCircleMarked />
                      ) : (
                        <BiRadioCircle />
                      )}
                    </div>
                    <div data-product-header-data-is-loading={!isReady}>
                      <h3 className="font-bold text-lg">
                        Ritual+ Delivery -{" "}
                        {formatCurrency(
                          Math.min(
                            oneTimePurchasePrice,
                            subscriptionPurchasePrice!
                          )
                        )}{" "}
                        {subscriptionPurchasePrice <= oneTimePurchasePrice && (
                          <small className="font-browser font-normal text-sm text-orange">
                            (save{" "}
                            {formatCurrency(
                              originalRetailPrice! - subscriptionPurchasePrice!
                            )}
                            )
                          </small>
                        )}
                        {subscriptionPurchasePrice > oneTimePurchasePrice && (
                          <small className="font-browser font-normal text-sm text-orange">
                            (renews at{" "}
                            {formatCurrency(subscriptionPurchasePrice)})
                          </small>
                        )}
                      </h3>
                      <p className="font-sans text-sm tracking-wide">
                        Free shipping, choose your delivery schedule, always get
                        our best deal, and cancel any time!
                      </p>
                      {selectedPurchaseType === "subscription" && (
                        <>
                          {minimumQuantityForSubscription &&
                            minimumQuantityForSubscription > 1 && (
                              <p>
                                Minimum of {minimumQuantityForSubscription}{" "}
                                required for a Ritual+ of {productName}
                              </p>
                            )}
                          <div className="items-start flex justify-start">
                            <div className="font-browser text-lg mt-2 mb-4 w-[6rem] md:w-[8rem]">
                              <p className="font-bold mb-1 text-sm">Quantity</p>
                              <QuantitySelector
                                onSetQuantity={onChangeQuantity}
                                quantity={selectedQuantity}
                              />
                            </div>
                            <div className="font-browser text-lg ml-4 mt-2 mb-4 w-[12rem] md:w-[14rem]">
                              <p className="font-bold mb-1 text-sm">
                                Deliver {productName}
                              </p>
                              <RitualFrequencySelect
                                onChange={(ev) =>
                                  onChangeFrequency(Number(ev.target.value))
                                }
                                frequency={selectedFrequencyInWeeks}
                              />
                            </div>
                          </div>
                          <div className="mb-8">
                            <Button
                              disabled={isAddingToBag || !isReady}
                              onClick={() => onAddToCart()}
                              size="small"
                              text={
                                isAddingToBag
                                  ? "Adding to your cart"
                                  : `Add to Cart | ${formatCurrency(
                                      selectedQuantity *
                                        subscriptionPurchasePrice
                                    )}`
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    className="items-start flex cursor-pointer mt-2"
                    onClick={() => onChangeSelectedPurchaseType("one-time")}
                  >
                    <div className="pt-1 pr-1">
                      {selectedPurchaseType === "one-time" ? (
                        <BiRadioCircleMarked />
                      ) : (
                        <BiRadioCircle />
                      )}
                    </div>
                    <div data-product-header-data-is-loading={!isReady}>
                      <h3 className="font-bold text-lg">
                        Purchase Once - {formatCurrency(oneTimePurchasePrice)}{" "}
                        {originalRetailPrice &&
                          originalRetailPrice > oneTimePurchasePrice && (
                            <small className="text-sm text-orange font-bold">
                              (
                              <span className="text-gray-400 line-through">
                                {formatCurrency(originalRetailPrice)}
                              </span>{" "}
                              - save{" "}
                              {formatCurrency(
                                originalRetailPrice! - oneTimePurchasePrice
                              )}
                              )
                            </small>
                          )}
                      </h3>
                      <p className="hidden font-sans text-sm tracking-wide">
                        or 4 interest-free payments of{" "}
                        <strong>
                          {formatCurrency(
                            0.25 * oneTimePurchasePrice * selectedQuantity
                          )}
                        </strong>{" "}
                        with sezzle
                      </p>
                      {selectedPurchaseType === "one-time" && (
                        <>
                          <div className="font-browser text-lg mt-1 mb-4 w-[140px]">
                            <p className="font-bold mb-1 text-sm">Quantity</p>
                            <QuantitySelector
                              onSetQuantity={onChangeQuantity}
                              quantity={selectedQuantity}
                            />
                          </div>
                          <div className="mb-4">
                            <Button
                              disabled={isAddingToBag || !isReady}
                              onClick={() => onAddToCart()}
                              size="small"
                              text={
                                isAddingToBag
                                  ? "Adding to your cart"
                                  : `Add to cart | ${formatCurrency(
                                      selectedQuantity * oneTimePurchasePrice
                                    )}`
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
        <ReactCanvasConfetti
          className="fixed h-full left-0 pointer-events-none top-0 w-full z-[999999]"
          colors={colors}
          fire={!isAddingToBag}
          particleCount={150}
          spread={360}
          startVelocity={30}
          ticks={360}
          zIndex={0}
        />
      </div>
    </>
  )
}
